import { useTranslate } from '@refinedev/core';
import { TextField, DateField } from '@refinedev/antd';
import { Typography, Tag, Divider } from 'antd';
import type { ContentProductDetailOut } from '@/api/generated/schemas';
import {
  publishingStatusColors,
  productTypeColors,
} from '@/pages/content_catalog/products/constants';

const { Title } = Typography;

export const ProductGeneralTab = ({
  record,
}: {
  record: ContentProductDetailOut;
}) => {
  const t = useTranslate();

  return (
    <>
      <Title level={4}>{t('base.sections.basic_info')}</Title>

      <Title level={5}>{t('base.fields.id')}</Title>
      <TextField value={record.id} />

      <Title level={5}>{t('content_catalog.products.fields.title')}</Title>
      <TextField value={record.title ?? '-'} />

      {record.subtitle && (
        <>
          <Title level={5}>
            {t('content_catalog.products.fields.subtitle')}
          </Title>
          <TextField value={record.subtitle} />
        </>
      )}

      {record.description && (
        <>
          <Title level={5}>
            {t('content_catalog.products.fields.description')}
          </Title>
          <Typography.Paragraph
            style={{
              maxWidth: 680,
              fontSize: 15,
              lineHeight: 1.75,
              letterSpacing: '0.01em',
              fontFamily: 'Georgia, "Times New Roman", serif',
            }}
          >
            {record.description}
          </Typography.Paragraph>
        </>
      )}

      <Title level={5}>
        {t('content_catalog.products.fields.product_type')}
      </Title>
      {record.product_type ? (
        <Tag color={productTypeColors[record.product_type] || 'default'}>
          {t(`content_catalog.products.product_type.${record.product_type}`)}
        </Tag>
      ) : (
        <TextField value="-" />
      )}

      <Title level={5}>
        {t('content_catalog.products.fields.publishing_status')}
      </Title>
      {record.publishing_status ? (
        <Tag
          color={publishingStatusColors[record.publishing_status] || 'default'}
        >
          {t(
            `content_catalog.products.publishing_status.${record.publishing_status}`
          )}
        </Tag>
      ) : (
        <TextField value="-" />
      )}

      <Divider />

      <Title level={4}>
        {t('content_catalog.products.sections.identifiers')}
      </Title>

      <Title level={5}>{t('content_catalog.products.fields.isbn')}</Title>
      <TextField value={record.isbn ?? '-'} />

      {record.ean && (
        <>
          <Title level={5}>{t('content_catalog.products.fields.ean')}</Title>
          <TextField value={record.ean} />
        </>
      )}

      {record.issn && (
        <>
          <Title level={5}>{t('content_catalog.products.fields.issn')}</Title>
          <TextField value={record.issn} />
        </>
      )}

      <Divider />

      <Title level={5}>{t('content_catalog.products.fields.language')}</Title>
      <TextField value={record.language ?? '-'} />

      {record.edition && (
        <>
          <Title level={5}>
            {t('content_catalog.products.fields.edition')}
          </Title>
          <TextField value={record.edition} />
        </>
      )}

      <Title level={5}>
        {t('content_catalog.products.fields.publication_id')}
      </Title>
      <TextField value={record.publication_id ?? '-'} />

      <Title level={5}>
        {t('content_catalog.products.fields.publisher_id')}
      </Title>
      {record.publisher_id ? (
        <Typography.Link
          href={`/content_catalog/publishers/${record.publisher_id}`}
        >
          {record.publisher_name ?? record.publisher_id}
        </Typography.Link>
      ) : (
        <TextField value="-" />
      )}

      {record.imprint_name && (
        <>
          <Title level={5}>
            {t('content_catalog.products.fields.imprint_name')}
          </Title>
          <TextField value={record.imprint_name} />
        </>
      )}

      {record.published_at && (
        <>
          <Title level={5}>
            {t('content_catalog.products.fields.published_at')}
          </Title>
          <DateField value={record.published_at} format="LL" />
        </>
      )}

      {record.withdrawn_at && (
        <>
          <Title level={5}>
            {t('content_catalog.products.fields.withdrawn_at')}
          </Title>
          <DateField value={record.withdrawn_at} format="LL" />
        </>
      )}

      <Divider />

      <Title level={4}>{t('base.sections.system_info')}</Title>

      <Title level={5}>{t('base.fields.created_at')}</Title>
      <DateField value={record.created_at} format="LLL" />

      <Title level={5}>{t('base.fields.updated_at')}</Title>
      <DateField value={record.updated_at} format="LLL" />
    </>
  );
};
